<template>
	<div
		:style="`width:${props.width}px; height:${props.height}px; position: relative; display: flex; align-items: center; justify-content: center;`">
		<i v-show="loading" class="fa-solid fa-spinner fa-spin fa-2xl p-10">
		</i>
		<video
			class="video"
			v-show="!loading"
			:id="id"
			autoplay
			playsinline></video>
		<div v-if="connectionWarning" class="connection-warning">
			{{ connectionWarning }}
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onUnmounted, ref } from 'vue';
	import { usePage } from '@inertiajs/vue3';
	import { WebRTCPlayer } from '@eyevinn/webrtc-player';
	import {
		createStreamingTelemetrySession,
		generateSessionId,
	} from '../../utils/StreamingTelemetry';

	type IceServer = { urls: string; username?: string; credential?: string };

	function resolveIceServers(): IceServer[] {
		const shared = (usePage().props as Record<string, unknown>)
			.streaming_ice_servers;
		if (!Array.isArray(shared)) return [];
		return shared.filter(
			(s): s is IceServer =>
				!!s && typeof (s as { urls?: unknown }).urls === 'string'
		);
	}

	const props = defineProps({
		muted: { type: Boolean, required: false, default: true },
		source_url: { type: String, required: true },
		height: { type: Number, required: false, default: 320 },
		width: { type: Number, required: false, default: 240 },
		auto: { type: Boolean, required: false, default: true },
		controls: { type: Boolean, required: false, default: false },
		streaming_auth_credential: { type: String, required: true },
		max_retries: { type: Number, required: false, default: 50 },
		retry_delay: { type: Number, required: false, default: 2000 },
		stall_timeout_ms: { type: Number, required: false, default: 5000 },
		// Optional — when provided, telemetry rows for this viewer carry the
		// exam id so we can query "who watched exam X". Parent components that
		// already know the id (proctor pages, exam pages) should pass it; other
		// callers can omit it.
		enrollment_test_id: { type: Number, required: false, default: null },
		stats_sample_interval_ms: {
			type: Number,
			required: false,
			default: 10000,
		},
	});

	const telemetry = createStreamingTelemetrySession({
		sessionId: generateSessionId(),
		enrollmentTestId: props.enrollment_test_id ?? null,
		actor: 'viewer',
	});
	telemetry.track('viewer.created', {
		source_url: props.source_url,
		stall_timeout_ms: props.stall_timeout_ms,
	});

	let lastPeerState: string = 'never';
	let statsInterval: ReturnType<typeof setInterval> | null = null;
	let firstFrameEmittedAt = 0;
	let viewerOpenedAt = Date.now();

	function emitPeerState(to: string, reason?: string) {
		telemetry.track('viewer.peer_state', {
			from: lastPeerState,
			to,
			...(reason ? { reason } : {}),
		});
		lastPeerState = to;
	}

	function stopStatsSampler() {
		if (statsInterval !== null) {
			clearInterval(statsInterval);
			statsInterval = null;
		}
	}

	function startStatsSampler(peer: RTCPeerConnection) {
		stopStatsSampler();
		statsInterval = setInterval(async () => {
			try {
				const report = await peer.getStats();
				telemetry.track('stats_snapshot', summarizeViewerStats(report));
			} catch {
				/* drop sampling errors silently */
			}
		}, props.stats_sample_interval_ms);
	}

	function summarizeViewerStats(
		report: RTCStatsReport
	): Record<string, unknown> {
		let bytesReceived = 0;
		let packetsReceived = 0;
		let packetsLost = 0;
		let jitterMs: number | null = null;
		let framesDecoded = 0;
		let framesDropped = 0;
		let framesPerSecond: number | null = null;
		let rttMs: number | null = null;
		let candidateType: string | null = null;
		let candidateProtocol: string | null = null;

		const byId = new Map<string, Record<string, unknown>>();
		report.forEach((stat: Record<string, unknown>) => {
			if (typeof stat.id === 'string') byId.set(stat.id, stat);
		});

		report.forEach((stat: Record<string, unknown>) => {
			const type = stat.type as string | undefined;
			if (type === 'inbound-rtp') {
				bytesReceived += Number(stat.bytesReceived ?? 0);
				packetsReceived += Number(stat.packetsReceived ?? 0);
				packetsLost += Number(stat.packetsLost ?? 0);
				const j = stat.jitter;
				if (typeof j === 'number') jitterMs = Math.round(j * 1000);
				if (typeof stat.framesDecoded === 'number')
					framesDecoded += stat.framesDecoded;
				if (typeof stat.framesDropped === 'number')
					framesDropped += stat.framesDropped;
				if (typeof stat.framesPerSecond === 'number')
					framesPerSecond = stat.framesPerSecond;
			} else if (
				type === 'candidate-pair' &&
				stat.nominated === true &&
				stat.state === 'succeeded'
			) {
				const rtt = stat.currentRoundTripTime;
				if (typeof rtt === 'number') rttMs = Math.round(rtt * 1000);
				const localCandidateId = stat.localCandidateId;
				if (typeof localCandidateId === 'string') {
					const local = byId.get(localCandidateId);
					if (local) {
						candidateType = (local.candidateType as string) ?? null;
						candidateProtocol = (local.protocol as string) ?? null;
					}
				}
			}
		});

		return {
			bytes_received: bytesReceived,
			packets_received: packetsReceived,
			packets_lost: packetsLost,
			jitter_ms: jitterMs,
			frames_decoded: framesDecoded,
			frames_dropped: framesDropped,
			frames_per_second: framesPerSecond,
			current_rtt_ms: rttMs,
			selected_candidate_type: candidateType,
			selected_candidate_protocol: candidateProtocol,
		};
	}

	const id = `video_${props.source_url}`;

	let loading = ref(false);
	let retryCount = ref(0);
	let retryTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
	let lastScheduleRetryIncrementAt = 0;

	const emit = defineEmits<{ started; failed; retrying }>();

	defineExpose({ takePhoto, start, stop });

	function getVideoElement(): HTMLVideoElement {
		return document.getElementById(id) as HTMLVideoElement;
	}

	function takePhoto() {
		const video = getVideoElement();

		const canvas = document.createElement('canvas');

		const context = canvas.getContext('2d');

		canvas.width = video.videoWidth;

		canvas.height = video.videoHeight;

		context?.drawImage(video, 0, 0, canvas.width, canvas.height);

		return canvas.toDataURL('image/jpeg').split(',')[1];
	}

	const video_player = ref();
	const connectionWarning = ref<string | null>(null);

	function clearRetryTimeout() {
		if (retryTimeout.value) {
			clearTimeout(retryTimeout.value);
			retryTimeout.value = null;
		}
	}

	function scheduleRetry() {
		clearRetryTimeout();
		if (retryCount.value < props.max_retries) {
			const now = Date.now();
			if (now - lastScheduleRetryIncrementAt >= 500) {
				retryCount.value++;
				lastScheduleRetryIncrementAt = now;
			}

			const delay = props.retry_delay;

			connectionWarning.value = `Reconectando... ${retryCount.value}/${props.max_retries}`;
			telemetry.track('viewer.retry', {
				attempt: retryCount.value,
				max_attempts: props.max_retries,
				delay_ms: delay,
			});
			emit('retrying', {
				attempt: retryCount.value,
				maxAttempts: props.max_retries,
			});

			retryTimeout.value = setTimeout(() => {
				start();
			}, delay);
		} else {
			connectionWarning.value = `Webcam com mau contato ou desconectada.`;
			telemetry.track('viewer.failed', {
				attempts: retryCount.value,
			});
			emit('failed', { attempts: retryCount.value });
			loading.value = false;
		}
	}

	async function start() {
		try {
			clearRetryTimeout();
			loading.value = true;

			if (retryCount.value === 0) {
				connectionWarning.value = null;
			}

			if (video_player.value) {
				try {
					await video_player.value.unload();
				} catch {
					/* ignore */
				}
				video_player.value = null;
			}

			const video = getVideoElement();

			const iceServers = resolveIceServers();

			const player = new WebRTCPlayer({
				video: video,
				type: 'whep',
				statsTypeFilter: '^candidate-*|^inbound-rtp',
				timeoutThreshold: props.stall_timeout_ms,
				...(iceServers.length ? { iceServers } : {}),
			});

			video.muted = props.muted;
			video.controls = props.controls;

			await player.load(
				new URL(props.source_url),
				props.streaming_auth_credential
			);

			let streamEverPlayed = false;

			const onFatalStreamError = (kind: string) => () => {
				telemetry.track('viewer.fatal_error', { kind });
				loading.value = false;
				scheduleRetry();
			};

			const peer = (player as unknown as { peer?: RTCPeerConnection })
				.peer;
			if (peer instanceof RTCPeerConnection) {
				peer.addEventListener('connectionstatechange', () => {
					const s = peer.connectionState;
					emitPeerState(s);
					if (s === 'connected') {
						startStatsSampler(peer);
					} else {
						stopStatsSampler();
					}
					if (s !== 'failed' && s !== 'disconnected') {
						return;
					}
					if (
						s === 'disconnected' &&
						!streamEverPlayed &&
						retryCount.value === 0
					) {
						return;
					}
					loading.value = false;
					scheduleRetry();
				});
			}

			player.on('no-media', () => {
				telemetry.track('viewer.no_media', {});
				loading.value = false;
				scheduleRetry();
			});

			player.on('peer-connection-failed', () => {
				telemetry.track('viewer.peer_connection_failed', {});
				loading.value = false;
				scheduleRetry();
			});

			player.on(
				'initial-connection-failed',
				onFatalStreamError('initial-connection-failed')
			);
			player.on('connect-error', onFatalStreamError('connect-error'));

			player.on('media-recovered', () => {
				telemetry.track('viewer.media_recovered', {});
				if (retryCount.value === 0) {
					connectionWarning.value = null;
				}
			});

			video.addEventListener('canplaythrough', () => {
				if (firstFrameEmittedAt === 0) {
					firstFrameEmittedAt = Date.now();
					telemetry.track('viewer.first_frame_at', {
						elapsed_ms: firstFrameEmittedAt - viewerOpenedAt,
					});
				}
				streamEverPlayed = true;
				loading.value = false;
				connectionWarning.value = null;
				retryCount.value = 0;
				lastScheduleRetryIncrementAt = 0;
				emit('started');
			});

			video.addEventListener('error', () => {
				telemetry.track('viewer.video_element_error', {});
				loading.value = false;
				scheduleRetry();
			});

			video_player.value = player;
		} catch (e) {
			loading.value = false;
			console.error('WebRTC Player Error:', e);
			scheduleRetry();
		}
	}

	async function stop() {
		telemetry.track('viewer.close', { reason: 'stop' });
		stopStatsSampler();
		clearRetryTimeout();
		connectionWarning.value = null;
		retryCount.value = 0;
		loading.value = false;

		if (video_player.value) {
			await video_player.value.unload();
		}
	}

	onMounted(async () => {
		if (props.auto) {
			await start();
		}
	});

	onUnmounted(() => {
		stopStatsSampler();
		clearRetryTimeout();
		telemetry.track('viewer.unmounted', {});
	});
</script>

<style scoped>
	.video {
		width: 100%;
		height: 100%;
	}
	.connection-warning {
		position: absolute;
		bottom: 10px;
		left: 0;
		right: 0;
		display: flex;
		justify-content: center;
		background: #ff9800;
		color: white;
		padding: 6px 12px;
		border-radius: 20px;
		font-size: 10px;
		font-weight: 500;
		text-align: center;
	}
</style>
